/**
 * «По дням» на экране «Лиды»: все лиды системы строкой на день, неделю или
 * месяц — сколько с рабочим номером, сколько уникальных и новых номеров и
 * сколько из них дошли до сделки, записи и брони. Клик по периоду — эти номера
 * во вкладке «По номерам». Спека `docs/specs/2026-09-30-leads-days-design.md`;
 * числа — те же, что у `prokat-runner leads-days`.
 *
 * Отбор «Месяцы» (`?months=6,8,9`, разметка 20.10.26): только эти месяцы каждого года
 * периода, строки одного месяца разных лет — подряд, итог — по ним же (считает бэкенд).
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker, type Окно, прошлыеОкна } from "@/components/DateRangePicker";
import { MultiPick } from "@/components/MultiPick";
import { iso, plusDays } from "@/lib/dates";
import { type LeadChannel, type LeadDay, type LeadDayCounts, loadLeadDays } from "@/lib/leadDays";

type Group = "day" | "week" | "month";
const GROUP: Record<Group, { label: string; head: string; hint: string }> = {
  day: { label: "по дням", head: "День", hint: "День — дата создания лида" },
  week: { label: "по неделям", head: "Неделя", hint: "Неделя — с понедельника, крайние обрезаны периодом" },
  month: { label: "по месяцам", head: "Месяц", hint: "Месяц — с 1-го числа, крайние обрезаны периодом" },
};
const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
/** «По номерам» считает не больше 62 дней. */
const PHONES_MAX_DAYS = 62;
/** Первый лид CRM — 26.07.2018. */
const FIRST_DAY = "2018-07-01";

/** Окна периода длиннее двух лет: сравнивать сезоны по годам. */
function окна(): Окно[] {
  const today = new Date();
  const t = iso(today);
  const годы = (n: number): Окно => ({
    label: `${n} ${n < 5 ? "года" : "лет"}`,
    hint: `${n * 365 - 1} дней назад и сегодня`,
    from: iso(plusDays(today, -(n * 365 - 1))),
    to: t,
  });
  return [...прошлыеОкна(), годы(3), годы(5), { label: "всё время", hint: "с первого лида CRM (июль 2018)", from: FIRST_DAY, to: t }];
}

const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const name = new Date(2026, i, 1).toLocaleDateString("ru-RU", { month: "long" });
  return { value: String(i + 1), label: name, short: name.slice(0, 3) };
});

type Count = Exclude<keyof LeadDayCounts, "by" | "funnel">;
type Col = { key: Count; head: string; hint: string; of?: Count; tone?: string; sep?: boolean };
/** Короткие заголовки каналов; чего нет — подпись из ответа. */
const CHANNEL_HEAD: Record<string, string> = { call: "Вх. звонок", callback: "Обр. звонок" };
const CHANNEL_HINT: Record<string, string> = {
  call: "номер завёл входящий звонок — рассылка «Лид выбрать время» таким не уходит",
  callback: "клиент заказал обратный звонок",
  popup: "заявка из попапа сайта",
  banner: "заявка из баннера сайта",
  other: "заказ с сайта, CRM-форма, чаты, вручную",
};
const COLS: Col[] = [
  { key: "leads", head: "Лидов", hint: "все лиды, созданные в периоде (без удалённых)" },
  { key: "valid_phone", head: "Рабочий номер", hint: "российский номер: 7 + код 3xx/4xx/8xx/9xx", of: "leads" },
  {
    key: "in_work",
    head: "Без мусора",
    hint: "с рабочим номером, кроме дублей, спама, рекламы, вакансий, курьеров, уведомлений и тестовых",
    of: "valid_phone",
    sep: true,
  },
  { key: "phones", head: "Уникальных номеров", hint: "номер в строке считается один раз" },
  {
    key: "new",
    head: "Новых",
    hint: "номер, по которому за 90 дней до его первого лида в строке лидов не было",
    of: "phones",
    sep: true,
  },
  { key: "deal", head: "Сделка", hint: "у лида номера есть сделка", of: "phones" },
  {
    key: "recorded",
    head: "Записали",
    hint: "сделка лида входила в «ждем клиента» после создания лида — по истории, не по стадии сейчас",
    of: "phones",
    tone: "text-ok",
  },
  {
    key: "booked",
    head: "Бронь",
    hint: "договор сделки лида перешёл в «Бронь»/«Подписан» после создания лида",
    of: "phones",
    tone: "text-ok",
  },
];

function pct(a: number, b: number): string {
  return b ? `${Math.round((a * 100) / b)}%` : "";
}

function dm(day: string): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}`;
}

function title(d: { day: string; to: string }, group: Group): string {
  if (group === "month") {
    const x = new Date(`${d.day}T12:00:00`);
    const name = `${x.toLocaleDateString("ru-RU", { month: "long" })} ${d.day.slice(0, 4)}`;
    const last = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
    return d.day.slice(8) === "01" && Number(d.to.slice(8)) === last ? name : `${name} · ${dm(d.day)} – ${dm(d.to)}`;
  }
  return d.day === d.to
    ? `${dm(d.day)} ${WEEKDAY[new Date(`${d.day}T12:00:00`).getDay()]}`
    : `${dm(d.day)} – ${dm(d.to)}`;
}

function span(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00`) - Date.parse(`${from}T12:00:00`)) / 86_400_000) + 1;
}

function Cell({
  v,
  of,
  tone,
  sep,
}: {
  v: number;
  of?: number | undefined;
  tone?: string | undefined;
  sep?: boolean | undefined;
}) {
  return (
    <td className={`whitespace-nowrap px-3 py-1.5 tabular-nums ${sep ? "border-r border-border" : ""}`}>
      <span className={`text-[14px] font-semibold ${v ? (tone ?? "text-fg") : "text-fg-faint"}`}>{v}</span>
      {of !== undefined && v > 0 && <span className="ml-1.5 text-[12px] text-fg-muted">{pct(v, of)}</span>}
    </td>
  );
}

/** Колонки, за которыми идёт разбивка по каналам: то же число по тому, кто завёл номер. */
type SplitKey = "phones" | "recorded" | "booked";
const SPLITS: Record<SplitKey, { head: string; hint: string }> = {
  phones: {
    head: "из них завёл",
    hint: "те же уникальные номера: кто завёл номер — заявка сайта или источник его первого лида в строке; процент — от уникальных",
  },
  recorded: {
    head: "записали — из каких каналов",
    hint: "те же записанные номера по тому, кто завёл номер; процент — от номеров этого канала",
  },
  booked: {
    head: "бронь — из каких каналов",
    hint: "те же номера с бронью по тому, кто завёл номер; процент — от номеров этого канала",
  },
};

function isSplit(key: Count): key is SplitKey {
  return key in SPLITS;
}

/** Есть ли чем раскладывать колонку: уникальные — по `by`, запись и бронь — по `funnel` (бэкенд с 07.10.26). */
function splitOf(key: Count, c: LeadDayCounts, channels: LeadChannel[]): key is SplitKey {
  return isSplit(key) && channels.length > 0 && (key === "phones" || c.funnel !== undefined);
}

function Cells({ c, channels }: { c: LeadDayCounts; channels: LeadChannel[] }) {
  return (
    <>
      {COLS.map((col) => {
        const key = col.key;
        const split: SplitKey | null = splitOf(key, c, channels) ? key : null;
        return [
          <Cell key={key} v={c[key]} of={col.of && c[col.of]} tone={col.tone} sep={col.sep || split !== null} />,
          ...(split
            ? channels.map((ch, i) => (
                <Cell
                  key={`${key}:${ch.key}`}
                  v={split === "phones" ? (c.by?.[ch.key] ?? 0) : (c.funnel?.[ch.key]?.[split] ?? 0)}
                  of={split === "phones" ? c.phones : (c.funnel?.[ch.key]?.phones ?? 0)}
                  tone={col.tone}
                  sep={i === channels.length - 1}
                />
              ))
            : []),
        ];
      })}
    </>
  );
}

function PeriodCell({ d, group }: { d: { day: string; to: string }; group: Group }) {
  const text = title(d, group);
  if (span(d.day, d.to) > PHONES_MAX_DAYS) return <>{text}</>;
  return (
    <Link
      to={`/leads?tab=phones&pfrom=${d.day}&pto=${d.to}`}
      className="underline decoration-border underline-offset-2 hover:decoration-fg"
      title="номера периода во вкладке «По номерам»"
    >
      {text}
    </Link>
  );
}

export function LeadDaysSection() {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const from = params.get("from") || iso(plusDays(today, -29));
  const to = params.get("to") || iso(today);
  const group: Group = params.get("group") === "week" || params.get("group") === "month" ? (params.get("group") as Group) : "day";
  const set = (patch: Record<string, string>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const months = (params.get("months") ?? "").split(",").filter((m) => MONTHS.some((x) => x.value === m));
  const monthsKey = months.join(",");
  const q = useQuery({
    queryKey: ["leads-days", from, to, monthsKey],
    queryFn: () => loadLeadDays(from, to, monthsKey),
    placeholderData: (prev) => prev,
  });
  const seg = (on: boolean) =>
    `h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
      on ? "border-accent text-fg" : "border-border text-fg-muted hover:text-fg"
    }`;
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const r = q.data;
  const channels = r?.channels ?? [];
  const split = (c: Col) => r !== undefined && splitOf(c.key, r.total, channels);
  const head = (c: Col) => [
    <th
      key={c.key}
      rowSpan={2}
      className={`${th} align-bottom ${c.sep || split(c) ? "border-r border-border" : ""}`}
      title={c.hint}
    >
      {c.head}
    </th>,
    ...(split(c) && isSplit(c.key)
      ? [
          <th
            key={`${c.key}:by`}
            colSpan={channels.length}
            className={`${th} border-b border-r border-border pb-1 text-center`}
            title={SPLITS[c.key].hint}
            data-annot={`${c.head.toLowerCase()} по каналам`}
          >
            {SPLITS[c.key].head}
          </th>,
        ]
      : []),
  ];
  const rows: LeadDay[] = r ? (group === "month" ? r.months : group === "week" ? r.weeks : r.days) : [];
  // С отбором месяцев по месяцам — один и тот же месяц разных лет подряд (новые сверху), между месяцами черта.
  const compare = group === "month" && (r?.pick_months?.length ?? 0) > 0;
  const month = (d: LeadDay) => Number(d.day.slice(5, 7));
  const list = compare ? [...rows].sort((a, b) => month(b) - month(a) || b.day.localeCompare(a.day)) : rows;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3" data-annot="период лиды по дням">
        <DateRangePicker label="Период" from={from} to={to} windows={окна()} onChange={(a, b) => set({ from: a, to: b })} />
        <MultiPick
          label="Месяцы"
          values={months}
          options={MONTHS}
          all="все месяцы"
          // Сравнить месяцы по годам — это строки по месяцам: группировку включаем сами.
          onChange={(v) => set({ months: v.join(","), ...(v.length && group !== "month" && { group: "month" }) })}
        />
        <div className="flex gap-1" role="group" aria-label="Группировка">
          {(Object.keys(GROUP) as Group[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => set({ group: k === "day" ? "" : k })}
              aria-pressed={group === k}
              className={seg(group === k)}
            >
              {GROUP[k].label}
            </button>
          ))}
        </div>
      </div>
      {q.isFetching && (
        <div className="text-[12.5px] text-fg-muted">
          Считаю лиды по CRM…{span(from, to) > 400 ? " За годы — до полуминуты." : ""}
        </div>
      )}
      {q.isError && <div className="text-[12.5px] text-err">Не удалось получить лиды: {String(q.error.message)}</div>}
      {r && (
        <section
          className={`rounded-[var(--radius-card)] border border-border bg-bg-subtle ${q.isPlaceholderData ? "opacity-60" : ""}`}
          data-annot={`лиды ${GROUP[group].label}`}
        >
          <div className="overflow-x-auto">
            <table className="w-auto text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th rowSpan={2} className={`${th} pl-4 align-bottom`}>
                    {GROUP[group].head}
                  </th>
                  {COLS.map(head)}
                </tr>
                <tr>
                  {COLS.filter(split).map((c) =>
                    channels.map((ch, i) => (
                      <th
                        key={`${c.key}:${ch.key}`}
                        className={`${th} pt-1 ${i === channels.length - 1 ? "border-r border-border" : ""}`}
                        title={CHANNEL_HINT[ch.key] ?? ch.label}
                      >
                        {CHANNEL_HEAD[ch.key] ?? ch.label}
                      </th>
                    )),
                  )}
                </tr>
              </thead>
              <tbody>
                {list.map((d, i) => {
                  const wd = new Date(`${d.day}T12:00:00`).getDay();
                  const next = compare && i > 0 && month(list[i - 1] as LeadDay) !== month(d);
                  return (
                    <tr
                      key={d.day}
                      className={`border-t hover:bg-bg-hover ${next ? "border-t-2 border-border-strong" : "border-border"} ${group === "day" && (wd === 0 || wd === 6) ? "bg-bg" : ""}`}
                      data-annot={`${GROUP[group].head.toLowerCase()} ${d.day}`}
                    >
                      <td className={`whitespace-nowrap py-1.5 pl-4 pr-3 tabular-nums ${d.leads ? "" : "text-fg-faint"}`}>
                        <PeriodCell d={d} group={group} />
                      </td>
                      <Cells c={d} channels={channels} />
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-border font-medium" data-annot="итого за период">
                  <td className="py-2 pl-4 pr-3 text-fg-muted">
                    Итого{" "}
                    {r.from.slice(0, 4) === r.to.slice(0, 4) ? (
                      <PeriodCell d={{ day: r.from, to: r.to }} group="week" />
                    ) : (
                      `${dm(r.from)}.${r.from.slice(0, 4)} – ${dm(r.to)}.${r.to.slice(0, 4)}`
                    )}
                    {r.pick_months?.length ? (
                      <span className="ml-1 text-fg-faint">
                        · только {r.pick_months.map((m) => MONTHS[m - 1]?.short).join(", ")}
                      </span>
                    ) : null}
                  </td>
                  <Cells c={r.total} channels={channels} />
                </tr>
              </tbody>
            </table>
          </div>
          <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
            {GROUP[group].hint}. Лиды всех источников из CRM; номер в строке — один раз, итог — уникальные номера за
            весь период, не сумма строк. Проценты: рабочий номер — от лидов, без мусора — от рабочих, новых, сделка,
            записали и бронь — от уникальных номеров. «Из них завёл» — те же уникальные номера по каналу первого лида номера
            в строке: заявка сайта (попап, баннер), иначе источник лида; проценты — от уникальных. Рассылка «Лид
            выбрать время» входящим звонкам не уходит. Справа от «Записали» и «Бронь» — те же номера по каналам; там
            процент — от номеров этого канала (сколько из канала дошло до записи и брони). Записали и бронь — по истории
            после создания лида: записи в копии полные с 01.01.25, брони — с 01.09.25, в месяцах раньше эти колонки
            занижены. «Месяцы» — только выбранные месяцы каждого года: строки одного месяца подряд, итог — по ним же.
            Клик по периоду — эти номера во вкладке «По номерам» (до {PHONES_MAX_DAYS} дней).
          </div>
        </section>
      )}
    </>
  );
}
